import { share } from '../../connection';
import {
  type BlobRecord,
  BlobStorageBase,
  getBlobRecordSize,
  hasBlobRecordInlineBytes,
  type ListedBlobRecord,
} from '../../storage';
import { IDBConnection, type IDBConnectionOptions } from './db';
import { withIdbRetry } from './retry';

export class IndexedDBBlobStorage extends BlobStorageBase {
  static readonly identifier = 'IndexedDBBlobStorage';
  override readonly isReadonly = false;

  readonly connection = share(new IDBConnection(this.options));

  constructor(private readonly options: IDBConnectionOptions) {
    super();
  }

  get db() {
    return this.connection.inner.db;
  }

  override async get(key: string) {
    return withIdbRetry(this.connection, async () => {
      const trx = this.db.transaction(['blobs', 'blobData'], 'readonly');
      const blob = await trx.objectStore('blobs').get(key);
      const data = await trx.objectStore('blobData').get(key);
      await trx.done;

      if (!blob || blob.deletedAt) {
        return null;
      }

      return {
        ...blob,
        ...(data?.data ? { data: data.data } : {}),
      };
    });
  }

  override async set(blob: BlobRecord) {
    return withIdbRetry(this.connection, async () => {
      const trx = this.db.transaction(['blobs', 'blobData'], 'readwrite');
      await trx.objectStore('blobs').put({
        key: blob.key,
        mime: blob.mime,
        size: getBlobRecordSize(blob),
        createdAt: new Date(),
        deletedAt: null,
      });
      if (hasBlobRecordInlineBytes(blob)) {
        await trx.objectStore('blobData').put({
          key: blob.key,
          data: blob.data!,
        });
      }
      trx.commit();
      await trx.done;
    });
  }

  override async delete(key: string, permanently: boolean) {
    return withIdbRetry(this.connection, async () => {
      if (permanently) {
        const trx = this.db.transaction(['blobs', 'blobData'], 'readwrite');
        await trx.objectStore('blobs').delete(key);
        await trx.objectStore('blobData').delete(key);
        trx.commit();
        await trx.done;
      } else {
        const trx = this.db.transaction('blobs', 'readwrite');
        const blob = await trx.store.get(key);
        if (blob) {
          await trx.store.put({
            ...blob,
            deletedAt: new Date(),
          });
        }
        trx.commit();
        await trx.done;
      }
    });
  }

  override async release() {
    return withIdbRetry(this.connection, async () => {
      const trx = this.db.transaction(['blobs', 'blobData'], 'readwrite');

      const it = trx.objectStore('blobs').iterate();

      for await (const item of it) {
        if (item.value.deletedAt) {
          await item.delete();
          await trx.objectStore('blobData').delete(item.value.key);
        }
      }
      trx.commit();
      await trx.done;
    });
  }

  override async list() {
    return withIdbRetry(this.connection, async () => {
      const trx = this.db.transaction('blobs', 'readonly');
      const it = trx.store.iterate();

      const blobs: ListedBlobRecord[] = [];
      for await (const item of it) {
        if (!item.value.deletedAt) {
          blobs.push(item.value);
        }
      }

      await trx.done;
      return blobs;
    });
  }
}
